import { useCallback, useEffect, useRef, useState } from "react";
import {
  listPractitionersPage,
  type Practitioner,
  type PractitionerListMeta,
  type PractitionerListParams,
} from "@/features/practitioners/api";
import { ApiError } from "@/lib/api";

const EMPTY_META: PractitionerListMeta = { count: 0, next: null, previous: null };

interface UsePractitionersResult {
  practitioners: Practitioner[];
  meta: PractitionerListMeta;
  loading: boolean;
  error: string | null;
  params: PractitionerListParams;
  /** Merge a partial update into the query params (triggers a refetch). */
  setParams: (patch: Partial<PractitionerListParams>) => void;
  refetch: () => void;
}

/**
 * Owns the practitioners-list query params and fetches a page whenever they
 * change. Mirrors `useClients`: `lastKeyRef` collapses StrictMode's double
 * invoke and skips no-op fetches; `requestIdRef` applies only the latest
 * response so a slow earlier request can't clobber a newer one.
 */
export function usePractitioners(
  initial: PractitionerListParams = {},
): UsePractitionersResult {
  const [params, setParamsState] = useState<PractitionerListParams>({
    page: 1,
    pageSize: 25,
    ordering: "last_name",
    ...initial,
  });
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [meta, setMeta] = useState<PractitionerListMeta>(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const setParams = useCallback((patch: Partial<PractitionerListParams>) => {
    setParamsState((prev) => ({ ...prev, ...patch }));
  }, []);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${JSON.stringify(params)}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    listPractitionersPage(params)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setPractitioners(result.practitioners);
        setMeta(result.meta);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setPractitioners([]);
        setMeta(EMPTY_META);
        setError(err instanceof ApiError ? err.message : "Failed to load practitioners.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [params, reloadKey]);

  return { practitioners, meta, loading, error, params, setParams, refetch };
}
